/**
 * Pure gates for committed-frame playback advance + effect crossings.
 */
import type { PerfCommitSample } from "./perf-frame-sampler.js";

export interface PlaybackAdvanceAssertion {
  ok: boolean;
  failures: string[];
  editTickMin: number | null;
  editTickMax: number | null;
  distinctEditTicks: number;
  editSpan: number;
  transitionsCrossed: number;
}

export interface EffectCrossingRanges {
  sfxAAtTick: number | null;
  sfxBAtTick: number | null;
  stickerStartTick: number | null;
  stickerEndTick: number | null;
  motionBeatStartTick: number | null;
  motionBeatEndTick: number | null;
  transitionBoundaryEditTick: number | null;
}

export interface EffectCrossingAssertion {
  ok: boolean;
  failures: string[];
  crossed: {
    sfxA: boolean;
    sfxB: boolean;
    sticker: boolean;
    motionBeat: boolean;
    transitionBoundary: boolean;
  };
}

function distinctSorted(nums: number[]): number[] {
  return [...new Set(nums.map((n) => Math.floor(n)))].sort((a, b) => a - b);
}

/**
 * Require committed edit ticks to advance materially during playback.
 * Scrubber-only advance is insufficient — samples must show end > start.
 */
export function assertCommittedPlaybackAdvance(
  samples: PerfCommitSample[],
  opts: { minDistinctEditTicks: number; minEditSpan: number },
): PlaybackAdvanceAssertion {
  const failures: string[] = [];
  if (!samples.length) {
    return {
      ok: false,
      failures: ["no committed samples for playback advance"],
      editTickMin: null,
      editTickMax: null,
      distinctEditTicks: 0,
      editSpan: 0,
      transitionsCrossed: 0,
    };
  }
  const edits = samples.map((s) => s.editTick);
  const distinct = distinctSorted(edits);
  const editTickMin = distinct[0]!;
  const editTickMax = distinct[distinct.length - 1]!;
  const editSpan = editTickMax - editTickMin;
  let transitionsCrossed = 0;
  for (let i = 1; i < samples.length; i++) {
    if (samples[i]!.editTick !== samples[i - 1]!.editTick) transitionsCrossed += 1;
  }

  if (editTickMax <= editTickMin) {
    failures.push(
      `committed edit ticks frozen (min=${editTickMin} max=${editTickMax}) — reject scrubber-only evidence`,
    );
  }
  if (distinct.length < opts.minDistinctEditTicks) {
    failures.push(
      `distinct edit ticks ${distinct.length} < ${opts.minDistinctEditTicks}`,
    );
  }
  if (editSpan < opts.minEditSpan) {
    failures.push(`edit tick span ${editSpan} < ${opts.minEditSpan}`);
  }
  if (transitionsCrossed < Math.max(1, opts.minDistinctEditTicks - 1)) {
    failures.push(
      `edit tick transitions ${transitionsCrossed} too few for advancing playback`,
    );
  }

  return {
    ok: failures.length === 0,
    failures,
    editTickMin,
    editTickMax,
    distinctEditTicks: distinct.length,
    editSpan,
    transitionsCrossed,
  };
}

function crossedTick(edits: number[], at: number): boolean {
  const min = Math.min(...edits);
  const max = Math.max(...edits);
  return min <= at && max >= at;
}

function overlappedInterval(edits: number[], start: number, end: number): boolean {
  if (end <= start) return false;
  return edits.some((t) => t >= start && t < end);
}

/**
 * Fail if lanes were merely present in the graph but never active during the
 * committed sample window.
 */
export function assertEffectCrossings(
  samples: PerfCommitSample[],
  ranges: EffectCrossingRanges,
): EffectCrossingAssertion {
  const failures: string[] = [];
  const edits = samples.map((s) => s.editTick);
  const crossed = {
    sfxA: false,
    sfxB: false,
    sticker: false,
    motionBeat: false,
    transitionBoundary: false,
  };

  if (!edits.length) {
    return {
      ok: false,
      failures: ["no committed samples for effect crossings"],
      crossed,
    };
  }

  if (ranges.sfxAAtTick == null) failures.push("sfx-a tick missing for crossing");
  else {
    crossed.sfxA = crossedTick(edits, ranges.sfxAAtTick);
    if (!crossed.sfxA) failures.push(`sample never crossed sfx-a atTick=${ranges.sfxAAtTick}`);
  }

  if (ranges.sfxBAtTick == null) failures.push("sfx-b tick missing for crossing");
  else {
    crossed.sfxB = crossedTick(edits, ranges.sfxBAtTick);
    if (!crossed.sfxB) failures.push(`sample never crossed sfx-b atTick=${ranges.sfxBAtTick}`);
  }

  if (ranges.stickerStartTick == null || ranges.stickerEndTick == null) {
    failures.push("sticker range missing for crossing");
  } else {
    crossed.sticker = overlappedInterval(edits, ranges.stickerStartTick, ranges.stickerEndTick);
    if (!crossed.sticker) {
      failures.push(
        `sample never overlapped sticker [${ranges.stickerStartTick},${ranges.stickerEndTick})`,
      );
    }
  }

  if (ranges.motionBeatStartTick == null || ranges.motionBeatEndTick == null) {
    failures.push("motion beat range missing for crossing");
  } else {
    crossed.motionBeat = overlappedInterval(
      edits,
      ranges.motionBeatStartTick,
      ranges.motionBeatEndTick,
    );
    if (!crossed.motionBeat) {
      failures.push(
        `sample never overlapped motion beat [${ranges.motionBeatStartTick},${ranges.motionBeatEndTick})`,
      );
    }
  }

  if (ranges.transitionBoundaryEditTick == null) {
    failures.push("transition boundary missing for crossing");
  } else {
    crossed.transitionBoundary = crossedTick(edits, ranges.transitionBoundaryEditTick);
    if (!crossed.transitionBoundary) {
      failures.push(
        `sample never crossed transition boundary editTick=${ranges.transitionBoundaryEditTick}`,
      );
    }
  }

  return { ok: failures.length === 0, failures, crossed };
}
